import { useEffect } from 'react';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useFormApi, useFormState } from 'informed';
import { addMonths } from 'date-fns';
import {
  Datepicker,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { BoxTableCell, BoxTableRow } from 'components/common/box';
import { Button, User } from 'components/common';
import { Icon } from 'components/common/icons';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { UserWithBasicData } from 'shared-types';
import type { PageState } from 'types';
import type { AchievementCompletionAddFormValues } from './AchievementCompletionsAdd';

interface Props {
  achievement: Pick<
    PageState['achievement'],
    'monthsBeforeExpiration' | 'type' | 'userAttributes'
  >;
  id: number | string;
  isAlreadyAddedUser: boolean;
  isAlreadyAddedUserInprogress: boolean;
  existingUser: UserWithBasicData;
  completionDate?: string;
  email?: string;
  expirationDate?: string;
  setUploader: (id: string, uploader: Function) => void;
  username?: string;
}

const AchievementCompletionsAddTableRow = ({
  achievement,
  id,
  isAlreadyAddedUser,
  isAlreadyAddedUserInprogress,
  existingUser,
  completionDate = null,
  email,
  expirationDate = null,
  setUploader,
  username
}: Props) => {
  const formApi = useFormApi();
  const formState = useFormState<AchievementCompletionAddFormValues>();

  const { messages, theme } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  useEffect(() => {
    if (isAlreadyAddedUserInprogress) return;

    if (hasCompletion) {
      formApi.setValue(`${id}.completionDate`, completionDate);
    }

    if (hasExpiration) {
      formApi.setValue(`${id}.expirationDate`, expirationDate);
    }
  }, [completionDate, expirationDate]);

  const handleCompletionDateChange = () => {
    const { completionDate } = formState.values[id];

    if (achievement.monthsBeforeExpiration) {
      formApi.setValue(
        `${id}.expirationDate`,
        completionDate
          ? addMonths(completionDate, achievement.monthsBeforeExpiration)
          : null
      );
    }
  };

  const handleRemoveUser = () => {
    const { emails, usernames, userSelector } = formState.values;

    const referenceValue = email || username;
    const arrayOfValues =
      userSelector === 'emails' ? [...emails] : [...usernames];

    const indexOfReference = arrayOfValues.indexOf(referenceValue);
    arrayOfValues.splice(indexOfReference, 1);

    formApi.setValue(
      userSelector === 'emails' ? 'emails' : 'usernames',
      arrayOfValues
    );
  };

  return (
    <BoxTableRow>
      <BoxTableCell>
        <Button
          category="light"
          icon="trash-alt"
          onClick={handleRemoveUser}
          size="small"
          tooltip={messages.commonRemoveUser}
        />
      </BoxTableCell>
      <BoxTableCell
        sticky={true}
        stickyBackgroundColor="white"
        style={{ zIndex: 10 }}
      >
        <div style={{ minWidth: 200, paddingRight: 20 }}>
          {existingUser ? (
            <div>
              <User enableProfileLink={true} user={existingUser} />
              {isAlreadyAddedUser || isAlreadyAddedUserInprogress ? (
                <div style={{ fontSize: '1.3rem', marginTop: 5 }}>
                  <div
                    style={{
                      alignItems: 'center',
                      backgroundColor: theme.warningLightColor,
                      borderRadius: 20,
                      color: theme.warningLightTextColor,
                      display: 'inline-flex',
                      fontSize: '1rem',
                      justifyContent: 'center',
                      marginRight: '5px',
                      width: 20,
                      height: 20
                    }}
                  >
                    <Icon icon="exclamation-triangle" />
                  </div>
                  {isAlreadyAddedUserInprogress
                    ? messages.usersUserAlreadyAddedInProgress
                    : messages.usersUserAlreadyAdded}
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              {email || username}
              <div
                style={{
                  color: theme.dangerColor,
                  fontSize: '1.3rem',
                  marginTop: 3
                }}
              >
                <Icon
                  icon="exclamation-circle"
                  style={{ fontSize: '1.2rem', marginRight: '5px' }}
                />
                {messages.usersAccountWillBeCreated}
              </div>
            </div>
          )}
        </div>
      </BoxTableCell>
      {isAlreadyAddedUserInprogress ? (
        <>
          <BoxTableCell></BoxTableCell>
          {hasStartedAt ? <BoxTableCell></BoxTableCell> : null}
          {hasCompletion ? <BoxTableCell></BoxTableCell> : null}
          {hasExpiration ? <BoxTableCell></BoxTableCell> : null}
          <BoxTableCell></BoxTableCell>
          {achievement?.userAttributes?.map((attribute) => (
            <BoxTableCell key={attribute.id}></BoxTableCell>
          ))}
        </>
      ) : (
        <>
          {hasStartedAt ? (
            <BoxTableCell>
              <Datepicker
                disabled={formState.values.useCommonStartedAtDate}
                field={`${id}.startedAtDate`}
                id={`${email || username}-started-at-date`}
                isClearable={true}
                placeholder={messages.publishStartDateEmpty}
                maxDate={
                  formState.values[id]?.completionDate ||
                  formState.values[id]?.expirationDate
                }
                simpleClear={true}
                style={{ width: 160 }}
              />
            </BoxTableCell>
          ) : null}

          {hasCompletion ? (
            <BoxTableCell>
              <Datepicker
                disabled={formState.values.useCommonCompletionDate}
                field={`${id}.completionDate`}
                id={`${email || username}-completionDate`}
                isClearable={true}
                placeholder={messages.completionsCompletionDateEmpty}
                maxDate={formState.values[id]?.expirationDate}
                minDate={formState.values[id]?.startedAtDate}
                onChange={handleCompletionDateChange}
                simpleClear={true}
                style={{ width: 160 }}
              />
            </BoxTableCell>
          ) : null}

          {hasExpiration ? (
            <BoxTableCell>
              <div
                {...(achievement.monthsBeforeExpiration && {
                  'aria-label':
                    messages.achievementCompletionAutomaticExpirationTooltip,
                  'data-tooltip':
                    messages.achievementCompletionAutomaticExpirationTooltip
                })}
              >
                <Datepicker
                  disabled={
                    achievement.monthsBeforeExpiration ||
                    formState.values.useCommonExpirationDate
                  }
                  field={`${id}.expirationDate`}
                  id={`${email || username}-expirationDate`}
                  isClearable={true}
                  placeholder={messages.publishEndDateEmpty}
                  minDate={
                    formState.values[id]?.completionDate ||
                    formState.values[id]?.startedAtDate
                  }
                  simpleClear={true}
                  style={{ width: 160 }}
                />
              </div>
            </BoxTableCell>
          ) : null}

          <BoxTableCell>
            <TextArea
              field={`${id}.comments`}
              id={`${id}-comments`}
              rows={2}
              style={{ minWidth: 200 }}
            />
          </BoxTableCell>

          <BoxTableCell>
            <MultipleAttachments
              disabled={formState.values.useCommonAttachments}
              getUploadHandler={(fn: Function) => setUploader(`${id}`, fn)}
              field={`${id}-attachments`}
              useUnwrapped={true}
              {...(formState.values.useCommonAttachments
                ? {
                    tooltip: messages.achievementUsersAddCommonAttachmentTooltip
                  }
                : {})}
            />
          </BoxTableCell>

          {achievement?.userAttributes?.map((attribute) => (
            <BoxTableCell
              key={attribute.id}
              valueContainerStyle={{ display: 'block', minWidth: 200 }}
            >
              <ProductUserCustomAttributeRenderFormField
                attribute={attribute}
                fieldParent={`${id}`}
              />
            </BoxTableCell>
          ))}
        </>
      )}
    </BoxTableRow>
  );
};

export default AchievementCompletionsAddTableRow;
